// ModelCore.js
import React, { useState, useEffect, useRef, forwardRef } from 'react';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import IfcScene from '../ifc-scene/scene';
import previewImg from '../../assets/icons/usdz-preview.png';

const ModelCore = forwardRef(function ModelCore({ model }, ref) {
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRotation(prev => {
        const { x, y, z } = prev;
        return {
          x: (x + 45) % 90,
          y: (y + 45) % 90,
          z: (z + 45) % 90
        };
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  const UsdzScene = () => {
    const a = document.createElement('a');
    if (a.relList.supports('ar')) {
      // AR is available.
      return (
        <a href={model?.url} rel="ar" className="ifc-model-ctn">
          <img
            alt="preview for usdz viewer"
            src={previewImg}
            className="ifc-model-preview"
          />
        </a>
      );
    } else {
      return <div>device not supported</div>;
    }
  };

  return (
    <>
      {model?.type === 'glb' ? (
        // Forward the ref so the parent can control the <model-viewer> element.
        <model-viewer
          ref={ref}
          style={{ width: '100%', height: '100%' }}
          camera-controls
          autoplay
          alt="GLB model viewer"
          src={model?.url}
        ></model-viewer>
      ) : null}
      {model?.type === 'ifc' ? <IfcScene url={model?.url} /> : null}
      {model?.type === 'usdz' ? UsdzScene() : null}
      {model?.type === 'obj' ? <ObjScene url={model?.url} /> : null}
    </>
  );
});

function ObjScene({ url }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const scene = new THREE.Scene();

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0x404040, 1);
    scene.add(ambientLight);

    // Add directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(1, 1, 1).normalize();
    scene.add(directionalLight);
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer();
    containerRef.current.appendChild(renderer.domElement);

    const loader = new OBJLoader();
    loader.load(url, (object) => {
      scene.add(object);
    });

    camera.position.z = 1;

    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      // Clean up resources when component unmounts
      renderer.dispose();
      scene.dispose();
    };
  }, [url]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }}></div>;
}

export default ModelCore;
